import { useEffect, useState } from "react";
import Button from "@components/ui/buttons/Button";
import TextField from "@components/ui/textfields/TextField";
import { useDataStore } from "@store/user_org";
import { serviceboxAuthApi, ServiceBoxAuthError } from "@services/api/serviceboxSync";

const POLL_MS = 2 * 60 * 1000;

/**
 * ServiceBox refuse la connexion de Carvis (mot de passe expiré, identifiants refusés) :
 * tout ce qui passe par ServiceBox échoue (mise à jour, transferts, prise de RDV). Le
 * back pose le drapeau au premier refus et l'efface à la première connexion réussie ;
 * cette modale bloque l'application tant qu'il est posé. Le nouveau mot de passe est
 * essayé sur ServiceBox avant d'être enregistré.
 */
export default function ServiceBoxAuthModal() {
  const orgData = useDataStore((s) => s.orgData);
  const fetchOrgData = useDataStore((s) => s.fetchOrgData);
  const enabled = !!orgData?.servicebox?.enabled;
  const [error, setError] = useState<ServiceBoxAuthError | null>(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const check = () => serviceboxAuthApi.status()
      .then((e) => {
        if (!alive) return;
        setError((prev) => {
          // Levé ailleurs (autre poste, Réglages) : recharger l'org, sinon cet onglet
          // continue d'envoyer l'ancien mot de passe au proxy.
          if (prev && !e && orgData?.id) fetchOrgData(orgData.id).catch(() => {});
          return e;
        });
      })
      .catch(() => {});
    check();
    const id = setInterval(check, POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [enabled, orgData?.id]);

  useEffect(() => {
    if (error && !username) setUsername(error.username || orgData?.servicebox?.username || "");
  }, [error]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!enabled || !error) return null;

  const expired = /expir/i.test(error.reason || "");

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await serviceboxAuthApi.saveCredentials(username.trim(), password);
      setError(null);
      setPassword("");
      if (orgData?.id) fetchOrgData(orgData.id).catch(() => {});
    } catch (e: any) {
      setSaveError(e?.response?.data?.detail || "Enregistrement impossible, réessayez.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 10002, background: "rgba(17,24,39,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
    >
      <div style={{ background: "#fff", borderRadius: 16, width: 460, maxWidth: "100%", boxShadow: "0 20px 50px rgba(0,0,0,0.25)" }}>
        <div className="flex flex-col gap-4" style={{ padding: 24 }}>
          <div className="flex flex-row items-center gap-3">
            <svg width="24" height="24" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
              <circle cx="10" cy="10" r="9" fill="#DC2626" />
              <path d="M10 5v6M10 13.5v.5" stroke="white" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <span className="text-h1 font-semibold" style={{ color: "#111827" }}>
              {expired ? "Mot de passe ServiceBox expiré" : "Connexion ServiceBox refusée"}
            </span>
          </div>
          <div className="text-h3" style={{ color: "#525252", lineHeight: "20px" }}>
            {expired
              ? <>ServiceBox refuse la connexion de Carvis : le mot de passe du compte <b>{error.username}</b> a expiré. Changez-le d'abord sur Passweb, puis saisissez le nouveau ici.</>
              : <>ServiceBox refuse l'identifiant ou le mot de passe enregistré dans Carvis. Saisissez les identifiants actuels.</>}
            {" "}En attendant, la mise à jour ServiceBox, les transferts et les prises de RDV échouent.
          </div>
          <TextField label="Identifiant ServiceBox" value={username} onChange={setUsername} placeholder="Ex: DF09057" />
          <TextField label="Nouveau mot de passe" value={password} onChange={setPassword} type="password" />
          {saveError && (
            <div className="text-h3" style={{ color: "#B91C1C", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "8px 12px" }}>
              {saveError}
            </div>
          )}
          <Button
            label={saving ? "Vérification auprès de ServiceBox…" : "Vérifier et enregistrer"}
            onClick={save}
            loading={saving}
            disabled={saving || !username.trim() || !password}
          />
        </div>
      </div>
    </div>
  );
}
